import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Image, Accordion, Button, ListGroup } from 'react-bootstrap';
import config from '../../Common/Configurations/APIConfig';
import apiService from '../../Common/Configurations/APIService';
import './HajjDetails.css'; 
import HajjEnquiryModal from './HajjEnquiryModel';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
};

const HajjPackageHeader = ({ pkg }) => (
    <div className="hajj-package-header mb-3">
        <h1 className="fw-bold display-10">{pkg.packageName}</h1>
        <div className="d-flex flex-wrap gap-3 text-muted fs-5">
            <div className="icon-text">
                <i className="ri-map-pin-line me-2"></i>
                <span>{pkg.destinationsCover || 'Makkah, Madinah, Mina & Arafat'}</span>
            </div>
            {pkg.destination?.destinationName && (
                <div className="icon-text"><i className="ri-earth-line me-2"></i><span>{pkg.destination.destinationName}</span></div>
            )}
        </div>
    </div>
);

const HajjHighlightsBar = ({ pkg }) => (
    <Card className="hajj-highlights-bar mb-4">
        <Card.Body className="py-2">
            <Row className="text-center gy-3">
                <Col xs={6} md={3} className="highlight-item"><i className="ri-time-line"></i><strong>Duration</strong><span>{pkg.duration}</span></Col>
                <Col xs={6} md={3} className="highlight-item"><i className="ri-group-line"></i><strong>Group Size</strong><span>{pkg.noOfPersons} Persons</span></Col>
                <Col xs={6} md={3} className="highlight-item"><i className="ri-calendar-check-line"></i><strong>Start Date</strong><span>{formatDate(pkg.startedDate)}</span></Col>
                <Col xs={6} md={3} className="highlight-item"><i className="ri-price-tag-3-line"></i><strong>Price</strong><span>{pkg.currency} {pkg.price.toLocaleString('en-IN')}</span></Col>
            </Row>
        </Card.Body>
    </Card>
);

const EnquiryBar = ({ onEnquiryClick }) => (
    <Card className="enquiry-card-section my-4">
        <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
                <h5 className="mb-1 fw-bold">Ready for the Journey of a Lifetime?</h5>
                <p className="mb-0">Our Hajj experts are available to assist you. Enquire now for details.</p>
            </div>
            <Button variant="primary" size="lg" onClick={onEnquiryClick}>Send Enquiry</Button>
        </Card.Body>
    </Card>
);

const DetailSection = ({ title, icon, children }) => (
    <div className="details-section mb-4"><h3 className="section-title"><i className={icon}></i> {title}</h3>{children}</div>
);

const ItineraryAccordion = ({ itineraries }) => {
    if (!itineraries || itineraries.length === 0) return null;
    const sortedItineraries = [...itineraries].sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true }));
    return (<DetailSection title="Daily Itinerary" icon="ri-calendar-todo-line"><Accordion defaultActiveKey="0" alwaysOpen>{sortedItineraries.map((item, index) => (<Accordion.Item eventKey={String(index)} key={item.packageItineraryId} className="mb-3"><Accordion.Header>{item.title}: {item.location}</Accordion.Header><Accordion.Body><p>{item.description}</p><hr /><p className="mb-1"><strong>Meals:</strong> {item.meals || 'Not specified'}</p>{item.note && <p className="mb-0 text-muted small"><strong>Note:</strong> {item.note}</p>}</Accordion.Body></Accordion.Item>))}</Accordion></DetailSection>);
};

const InclusionsExclusionsSection = ({ includes, excludes }) => {
    if ((!includes || includes.length === 0) && (!excludes || excludes.length === 0)) return null;
    return (<DetailSection title="Inclusions & Exclusions" icon="ri-task-line"><Row>{includes?.length > 0 && (<Col md={6}><h4 className="sub-section-title">What's Included</h4><ListGroup variant="flush" className="includes-list">{includes.map(item => <ListGroup.Item key={item.packageIncludeId}>{item.description}</ListGroup.Item>)}</ListGroup></Col>)}{excludes?.length > 0 && (<Col md={6}><h4 className="sub-section-title">What's Not Included</h4><ListGroup variant="flush" className="excludes-list">{excludes.map(item => <ListGroup.Item key={item.packageExcludeId}>{item.description}</ListGroup.Item>)}</ListGroup></Col>)}</Row></DetailSection>);
};

const TermsAndConditionsSection = ({ terms }) => {
    if (!terms || terms.length === 0) return null;
    return (<DetailSection title="Terms & Conditions" icon="ri-file-shield-2-line"><div className="terms-conditions-container">{terms.map(term => (<div key={term.packageTermsAndConditionId} className="terms-item mb-3"><strong className="terms-title">{term.title}</strong><p className="terms-description text-muted mb-0">{term.description}</p></div>))}</div></DetailSection>);
};

const HajjDetailsContent = ({ details, onEnquiryClick }) => {
    const { pkg, hotelBookings, flightBookings, itineraries, includes, excludes, termsandConditions } = { pkg: details.package, ...details };

    return (
        <Container>
            <Row>
                <Col lg={12}>
                    <HajjPackageHeader pkg={pkg} />
                    <HajjHighlightsBar pkg={pkg} />
                    <Image src={pkg.imageSrc} alt={pkg.packageName} fluid rounded className="main-package-image mb-4" />
                    <EnquiryBar onEnquiryClick={onEnquiryClick} />
                    <DetailSection title="About This Hajj Journey" icon="ri-information-line"><p>{pkg.description}</p></DetailSection>
                    <ItineraryAccordion itineraries={itineraries} />
                    {hotelBookings?.length > 0 && (<DetailSection title="Accommodation" icon="ri-hotel-bed-line"><Row>{hotelBookings.map(hotel => (<Col md={6} key={hotel.packageHotelBookingId} className="mb-3"><Card className="h-100 details-card"><Card.Body><Card.Title className="details-card-title">{hotel.hotelName}, {hotel.city}</Card.Title><Card.Text className="details-card-text"><strong>Nights:</strong> {hotel.nights}</Card.Text></Card.Body></Card></Col>))}</Row></DetailSection>)}
                    {flightBookings?.length > 0 && (<DetailSection title="Flight Details" icon="ri-plane-line">{flightBookings.map(flight => (<Card className="details-card mb-3" key={flight.packageFlightBookingId}><Card.Body><Card.Title className="details-card-title">{flight.airline} ({flight.flightNumber})</Card.Title><Card.Text className="details-card-text"><strong>From:</strong> {flight.departureCity} | <strong>To:</strong> {flight.arrivalCity}</Card.Text><Card.Text className="details-card-text"><strong>Departure:</strong> {formatDate(flight.departureDateTime)}</Card.Text></Card.Body></Card>))}</DetailSection>)}
                    <InclusionsExclusionsSection includes={includes} excludes={excludes} />
                    <TermsAndConditionsSection terms={termsandConditions} />
                </Col>
            </Row>
        </Container>
    );
}

const HajjDetails = () => {
    const { packageId } = useParams();
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEnquiryModal, setShowEnquiryModal] = useState(false);
    const [hasModalPoppedUp, setHasModalPoppedUp] = useState(false);

    useEffect(() => {
        const getPackageDetails = async () => {
            if (!packageId) return; setLoading(true);
            try {
                const response = await apiService.get(`${config.GETPACKAGEDETAILS}/${packageId}`);
                setDetails(response);
            } catch (err) {
                console.error("Failed to fetch package details:", err);
                setError("Could not load package details. Please try again later.");
            } finally {
                setLoading(false);
            }
        };
        getPackageDetails();
    }, [packageId]);

    useEffect(() => {
        if (!loading && details && !hasModalPoppedUp) {
            const timer = setTimeout(() => {
                setShowEnquiryModal(true);
                setHasModalPoppedUp(true);
            }, 3000); // 3-second delay
            return () => clearTimeout(timer);
        }
    }, [loading, details, hasModalPoppedUp]);

    return (
        <>
            <div className="main-content">
                <div className="page-content hajj-details-page">
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                            <Spinner animation="border" variant="primary" />
                            {/* Updated loading text */}
                            <span className="ms-3 fs-5">Loading Hajj Journey Details...</span>
                        </div>
                    ) : error ? (
                         <Container className="text-center py-5"><h2 className="text-danger">{error}</h2></Container>
                    ) : !details || !details.package ? (
                         <Container className="text-center py-5"><h2>Package not found.</h2></Container>
                    ) : (
                        <HajjDetailsContent details={details} onEnquiryClick={() => setShowEnquiryModal(true)} />
                    )}
                </div>
            </div>
            
            {details && (
                <HajjEnquiryModal 
                    show={showEnquiryModal} 
                    handleClose={() => setShowEnquiryModal(false)} 
                    packageDetails={details} 
                />
            )}
        </>
    );
};

export default HajjDetails;